<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuth } from "@/features/auth/composables/useAuth";
import { useGraph } from "@/features/profile/composables/useGraph";
import { mapGraphProfileToUserData } from "@/features/profile/services/graph-user-mapper";
import { useLogout } from "@/shared/composables/useLogout";
import {
  userDataCache,
  EDITABLE_FIELDS,
  OVERRIDABLE_FIELDS,
  ALL_OVERRIDE_FIELDS,
} from "@/shared/services/user-data-cache";
import type { UserOverrides } from "@/shared/services/user-data-cache";
import { profilePermissions } from "@/shared/services/profile-permissions";
import { templateCache, reconcileTemplateSlots } from "@/features/signature/services/template-cache";
import { flushRoaming } from "@/shared/services/storage";
import { fetchSignatureTemplates } from "@/features/signature/services/api";
import { getVariableMeta, collectTemplateFields } from "@/shared/services/variable-extractor";
import type { VariableMeta } from "@/shared/services/variable-extractor";
import { useToast } from "@/shared/composables/useToast";
import PhotoUpload from "@/features/profile/components/PhotoUpload.vue";
import ProfileForm from "@/features/profile/components/ProfileForm.vue";
import AppMessage from "@/shared/components/AppMessage.vue";
import type { GraphUserProfile } from "@/shared/types";

const router = useRouter();
const toast = useToast();
const { init, isLoggedIn } = useAuth();
const { doLogout } = useLogout();
const { photo,
  microsoftPhoto,
  photoChangedLocally,
  profile: graphProfile,
  isLoading: graphLoading,
  graphError,
  graphAuthExpired,
  loadProfile,
  uploadLocalPhoto,
  restoreMicrosoftPhoto,
  clearCache: clearGraphCache,
} = useGraph();

// Alimentam apenas o cabeçalho do card; o formulário lê de `editableValues`.
const formName = ref("");
const formEmail = ref("");
const formJobTitle = ref("");
const activeTab = ref<string>("contact");
const isSyncing = ref(false);
const editableValues = ref<Record<string, string>>({});
const allowEditing = ref(false);

/** Campos exibidos em cada aba, para esconder a aba que ficar sem nenhum. */
const CONTACT_FIELDS = ["display_name", "email", "job_title", "office_phone_1", "office_phone_2"];
const ADDRESS_FIELDS = ["street_address", "city", "state", "zip_code", "country"];

/**
 * Campos que alguma assinatura do usuário realmente renderiza.
 *
 * `null` enquanto não há templates em cache (primeiro acesso, offline, API fora)
 * — nesse caso o formulário mostra tudo, em vez de aparecer vazio por falta de
 * informação.
 */
const usedFields = ref<string[] | null>(null);

function isFieldVisible(name: string): boolean {
  return usedFields.value === null || usedFields.value.includes(name);
}

const editableFields = computed<VariableMeta[]>(() =>
  getVariableMeta([...EDITABLE_FIELDS].filter(isFieldVisible))
);

const tabs = computed(() =>
  [
    {
      id: "contact",
      label: "Contato",
      icon: "md-personpin-round",
      badge: 0,
      visible: CONTACT_FIELDS.some(isFieldVisible),
    },
    {
      id: "address",
      label: "Endereço",
      icon: "md-locationon",
      badge: 0,
      visible: ADDRESS_FIELDS.some(isFieldVisible),
    },
    {
      id: "extras",
      label: "Extras",
      icon: "md-info",
      badge: editableFields.value.length,
      visible: editableFields.value.length > 0,
    },
  ].filter((tab) => tab.visible)
);

let currentPhoto: string | null = null;

/**
 * Indica se há dados salvos suficientes para a tela funcionar sem o Graph.
 *
 * @returns `true` quando existe snapshot em cache.
 */
function hasRenderableUserData(): boolean {
  return userDataCache.get() !== null;
}

/**
 * Recalcula quais campos o formulário deve pedir, a partir dos templates que o
 * usuário tem. Sem templates em cache, mantém `null` — mostrar tudo é melhor do
 * que esconder um campo que a assinatura usa.
 */
function refreshUsedFields(): void {
  const templates = templateCache.get();
  if (!templates || templates.length === 0) {
    usedFields.value = null;
    return;
  }
  usedFields.value = Array.from(collectTemplateFields(templates.map((t) => t.html)));

  // A aba aberta pode ter deixado de existir depois do filtro.
  if (!tabs.value.some((tab) => tab.id === activeTab.value)) {
    activeTab.value = tabs.value[0]?.id ?? "";
  }
}

function applyGraphToCache(p: GraphUserProfile): void {
  userDataCache.refreshFromGraph(mapGraphProfileToUserData(p));
  userDataCache.setIdentityEmail(p.mail);
  void flushRoaming();
}

onMounted(async () => {
  await Office.onReady();
  await init();

  if (!isLoggedIn.value) {
    router.push("/login");
    return;
  }

  // force=true: sempre busca do Graph ao abrir o painel (não só quando o cache de 24h expira)
  await loadProfile(true);

  // Sessão expirada só expulsa quem não tem nada em cache para mostrar.
  // Com dados salvos o formulário continua utilizável offline.
  if (graphAuthExpired.value && !hasRenderableUserData()) {
    router.push("/login?reason=expired");
    return;
  }
  if (graphAuthExpired.value) {
    showMessage("Não foi possível atualizar seus dados do Microsoft 365. Exibindo os dados salvos.", "info");
  }

  if (photo.value) currentPhoto = photo.value;

  // Sincroniza a permissão de edição (allow_editing) direto da API: esta tela
  // pode ser a primeira aberta após o login (sem passar por /signatures), que é
  // o único outro lugar que busca templates e carrega esse flag.
  const emailForPermissionSync = graphProfile.value?.mail || userDataCache.identityEmail();
  if (emailForPermissionSync) {
    try {
      await fetchSignatureTemplates(emailForPermissionSync, graphProfile.value?.displayName);
    } catch {
      // Offline ou API indisponível — segue com a permissão já cacheada (padrão: negar).
    }
  }
  allowEditing.value = profilePermissions.allowEditing();
  refreshUsedFields();

  // Cache é a fonte autoritativa — carrega primeiro
  loadSavedUserInfo();

  if (graphProfile.value) {
    applyGraphToCache(graphProfile.value);
    loadSavedUserInfo();
  } else if (graphError.value) {
    showMessage("Não foi possível carregar seus dados do Microsoft 365.", "info");
  }
});

/**
 * Descarta alterações não salvas do formulário e retorna para assinaturas.
 */
function discardAndExit(): void {
  router.push("/signatures");
}

/**
 * Restaura os dados para o estado padrão: descarta os overrides do usuário
 * (Certificações e Ramal) e recompõe tudo a partir do Microsoft Graph.
 *
 * @returns Promise que resolve quando a restauração é concluída.
 *
 * @example
 * await restoreDefaults();
 */
async function restoreDefaults(): Promise<void> {
  if (isSyncing.value) return;
  isSyncing.value = true;

  try {
    userDataCache.clear();

    await loadProfile(true);

    if (graphAuthExpired.value && !hasRenderableUserData()) {
      router.push("/login?reason=expired");
      return;
    }

    if (graphProfile.value) {
      applyGraphToCache(graphProfile.value);
      loadSavedUserInfo();
      showMessage("Dados restaurados para os padrões.", "success");
    } else if (graphError.value) {
      showMessage(graphError.value || "Erro ao restaurar dados.", "error");
    }
  } catch {
    showMessage("Erro ao restaurar dados.", "error");
  } finally {
    isSyncing.value = false;
  }
}

function loadSavedUserInfo(): void {
  const cached = userDataCache.get();
  if (!cached) return;

  formName.value = cached.display_name || cached.name || "";
  formEmail.value = cached.email || "";
  formJobTitle.value = cached.job_title || cached.job || "";
  if (cached.photo) currentPhoto = cached.photo;

  // Semeia o formulário com o valor efetivo de cada campo: o override quando
  // existe, o derivado do Graph caso contrário. Assim os campos de Contato e
  // Endereço aparecem preenchidos e editar significa sobrescrever.
  const effective = cached as Record<string, string>;
  editableValues.value = Object.fromEntries(
    ALL_OVERRIDE_FIELDS.map((field) => [field, effective[field] ?? ""])
  );
}

function showMessage(text: string, type: "success" | "error" | "info"): void {
  if (type === "success") toast.success(text);
  else if (type === "error") toast.error(text);
  else toast.info(text);
}

async function onUploadPhoto(file: File): Promise<void> {
  await uploadLocalPhoto(file);
  currentPhoto = photo.value;
  toast.success("Foto atualizada. Lembre-se de Salvar para aplicar.");
}

function onRestorePhoto(): void {
  restoreMicrosoftPhoto();
  currentPhoto = photo.value;
  toast.info("Foto restaurada para a original do Microsoft 365.");
}

/**
 * Seleciona quais valores do formulário podem virar overrides.
 *
 * Os campos autorais da aba Extras ({@link EDITABLE_FIELDS}) são sempre gravados
 * não existem em nenhuma outra fonte.
 *
 * @param formValues - Valores vindos do formulário.
 * @returns Overrides a persistir.
 */
function collectOverrides(formValues: Record<string, string>): UserOverrides {
  const extras = Object.fromEntries(
    EDITABLE_FIELDS.map((field) => [field, formValues[field] ?? ""])
  );

  if (allowEditing.value) {
    return { ...formValues, ...extras } as UserOverrides;
  }

  const persisted = userDataCache.getOverrides() as Record<string, string>;
  const derived = Object.fromEntries(
    OVERRIDABLE_FIELDS.map((field) => [field, persisted[field] ?? ""])
  );
  return { ...derived, ...extras } as UserOverrides;
}

/**
 * Persiste os overrides do usuário e volta para a lista de assinaturas.
 *
 * Um campo deixado em branco perde o override e volta ao valor derivado do
 * Graph, que é recomposto a cada abertura.
 *
 * @param overrides - Valores dos campos editáveis vindos do formulário.
 */
function saveAndNavigate(overrides: Record<string, string> = {}): void {
  if (currentPhoto) {
    userDataCache.save({ ...userDataCache.get(), photo: currentPhoto, photo_base64: currentPhoto });
  }

  userDataCache.saveOverrides(collectOverrides(overrides));
  // Relê do cache: campos limpos precisam voltar ao derivado, não ficar vazios.
  loadSavedUserInfo();

  if (Office.context.roamingSettings) {
    reconcileTemplateSlots(templateCache.get() || []);

    void flushRoaming().then((result) => {
      if (!result.ok) {
        toast.error("Erro ao salvar: " + (result.error?.message || "desconhecido"));
      } else {
        toast.success("Dados salvos com sucesso!");
        setTimeout(() => router.push("/signatures"), 700);
      }
    });
  } else {
    // Fora do Outlook: salva apenas no cache local — ainda é um resultado útil
    toast.warning("Salvo localmente (sem roaming do Outlook).");
    setTimeout(() => router.push("/signatures"), 1500);
  }
}

async function handleLogout(): Promise<void> {
  try {
    clearGraphCache();
    await doLogout();
    toast.success("Você saiu da conta.");
  } catch (err: any) {
    toast.error("Erro ao sair da conta: " + (err?.message || "desconhecido"));
  }
}
</script>

<template>
  <div class="profile-container">
    <div class="profile-card">
      <header class="profile-card__header">
        <button
          class="profile-header__discard"
          type="button"
          title="Voltar sem salvar"
          aria-label="Voltar sem salvar"
          v-on:click="discardAndExit"
        >
          <v-icon name="md-arrowbackiosnew-outlined" />
        </button>

        <button
          class="profile-header__sync"
          type="button"
          v-bind:disabled="isSyncing"
          v-bind:title="isSyncing ? 'Restaurando...' : 'Restaurar para os padrões'"
          v-on:click="restoreDefaults"
        >
          <v-icon
            name="md-restore-outlined"
            v-bind:class="{ 'is-spinning': isSyncing }"
          />
        </button>

        <PhotoUpload
          :photo="photo"
          :microsoft-photo="microsoftPhoto"
          :photo-changed-locally="photoChangedLocally"
          :loading="graphLoading"
          @upload="onUploadPhoto"
          @restore="onRestorePhoto"
        />

        <div class="profile-card__identity">
          <h1 class="profile-card__name">{{ formName || "Seu nome" }}</h1>
          <p class="profile-card__meta">
            <v-icon name="md-workoutline" />
            <span>{{ formJobTitle || "Cargo não informado" }}</span>
          </p>
          <p class="profile-card__meta">
            <v-icon name="md-email-outlined" />
            <span>{{ formEmail || "E-mail não informado" }}</span>
          </p>
        </div>
      </header>

      <AppMessage v-if="graphError" :text="graphError" type="info" />

      <nav class="profile-tabs" role="tablist">
        <button
          v-for="tab in tabs"
          v-bind:key="tab.id"
          v-bind:class="['profile-tab', { 'profile-tab--active': activeTab === tab.id }]"
          v-bind:aria-selected="activeTab === tab.id"
          role="tab"
          type="button"
          v-on:click="activeTab = tab.id"
        >
          <v-icon v-bind:name="tab.icon" />
          <span>{{ tab.label }}</span>
          <span
            v-if="tab.badge"
            class="profile-tab__badge"
            aria-label="campos extras pendentes"
          >{{ tab.badge }}</span>
        </button>
      </nav>

      <ProfileForm
        :active-tab="activeTab"
        :editable-fields="editableFields"
        :editable-values="editableValues"
        :allow-editing="allowEditing"
        :used-fields="usedFields"
        @save="saveAndNavigate"
        @logout="handleLogout"
      />
    </div>
  </div>
</template>

<style scoped>
.profile-container {
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  padding: var(--spacing-md) var(--spacing-sm);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  overflow-x: hidden;
}

.profile-card {
  background: var(--colorBackground);
  border: 1px solid var(--colorBorderSubtle);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow4);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.profile-card > * {
  flex-shrink: 0;
}

.profile-form {
  flex: 1;
  min-height: 0;
}

.profile-card__header {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-xl) var(--spacing-lg) var(--spacing-lg);
  text-align: center;
  background: linear-gradient(
    180deg,
    var(--colorBackgroundSubtle) 0%,
    var(--colorBackground) 100%
  );
  border-bottom: 1px solid var(--colorBorderSubtle);
}

.profile-header__sync,
.profile-header__discard {
  position: absolute;
  top: var(--spacing-sm);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid var(--colorBorder);
  border-radius: 50%;
  background: var(--colorBackground);
  color: var(--colorTextSecondary);
  cursor: pointer;
  transition: background var(--transition), color var(--transition), border-color var(--transition), transform var(--transition);

  .ov-icon {
    width: 18px;
    height: 18px;
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid var(--colorBorderFocus);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.profile-header__discard {
  left: var(--spacing-sm);

  &:hover:not(:disabled) {
    background: var(--colorBackgroundHover);
    color: var(--colorPrimary);
    border-color: var(--colorPrimary);
  }
}

.profile-header__sync {
  right: var(--spacing-sm);

  &:hover:not(:disabled) {
    background: var(--colorBackgroundHover);
    color: var(--colorPrimary);
    border-color: var(--colorPrimary);
    transform: rotate(15deg);
  }
}

.is-spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.profile-card__identity {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.profile-card__name {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: 600;
  color: var(--colorText);
}

.profile-card__meta {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--colorTextSecondary);
}

.profile-card__meta .ov-icon {
  width: 14px;
  height: 14px;
  color: var(--colorTextTertiary);
}

.profile-tabs {
  display: flex;
  gap: var(--spacing-xs);
  padding: 0 var(--spacing-lg);
  border-bottom: 1px solid var(--colorBorderSubtle);
}

.profile-tab {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  font-weight: 500;
  color: var(--colorTextSecondary);
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
  cursor: pointer;
  position: relative;
  transition: color var(--transition), border-color var(--transition), background var(--transition);

  .ov-icon {
    width: 16px;
    height: 16px;
  }

  &:hover:not(.profile-tab--active) {
    color: var(--colorText);
    background: var(--colorBackgroundSubtle);
  }

  &--active {
    color: var(--colorPrimary);
    background: var(--colorBackgroundSubtle);
    border-bottom-color: var(--colorPrimary);
    font-weight: 600;

    span {
      color: var(--colorPrimary);
    }

    .ov-icon {
      color: var(--colorPrimary);
    }
  }
}

.profile-tab__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  font-size: 10px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--colorTextInverted);
  background: var(--colorPrimary);
  border-radius: 9px;
  line-height: 1;
}

.profile-tab--active .profile-tab__badge {
  background: var(--colorPrimary);
}

.profile-tab:not(.profile-tab--active) .profile-tab__badge {
  background: var(--colorTextTertiary);
}

@media (max-width: 400px) {
  .profile-container {
    padding: var(--spacing-sm) 0;
  }

  .profile-card__header {
    padding: var(--spacing-lg) var(--spacing-md) var(--spacing-md);
  }

  .profile-tabs {
    padding: 0 var(--spacing-md);
  }

  .profile-tab {
    padding: var(--spacing-sm) var(--spacing-sm);
    font-size: var(--font-size-sm);
  }
}
</style>
